// src/components/ReadingSessionTimer.jsx
// Single, prominent reading session timer

import React, { useState, useEffect, useRef } from 'react';
import { useReadingSession } from '../contexts/ReadingSessionContext';
import { MD3Button } from './Material3';
import Icon from './ui/Icon';

// Helper to constrain position within bounds
const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const ReadingSessionTimer = () => {
  const {
    activeSession,
    stopReadingSession,
    pauseReadingSession,
    resumeReadingSession,
    isPaused
  } = useReadingSession();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  // Dragging state
  const timerRef = useRef(null);
  const [pos, setPos] = useState({ x: 20, y: 20 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  // Update timer every second when session is active
  useEffect(() => {
    if (!activeSession) return;

    const updateTimer = () => {
      if (activeSession.isPaused) {
        // When paused, use accumulated time
        setElapsedTime(activeSession.accumulatedTime || 0);
      } else {
        // When active, calculate current elapsed time
        const startTime = new Date(activeSession.startTime);
        const now = new Date();
        const currentElapsed = Math.floor((now - startTime) / 1000);
        const totalElapsed = (activeSession.accumulatedTime || 0) + currentElapsed;
        setElapsedTime(totalElapsed);
      }
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [activeSession]);

  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Drag handlers
  const onPointerDown = (e) => {
    // Only start drag from header area
    if (!e.currentTarget.dataset.draggable) return;

    setDragging(true);
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    dragStart.current = { x: clientX, y: clientY };
    startPos.current = { ...pos };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;

    if (e.cancelable) e.preventDefault();

    const dx = clientX - dragStart.current.x;
    const dy = clientY - dragStart.current.y;

    // Constrain within viewport
    const node = timerRef.current;
    const w = node?.offsetWidth ?? 280;
    const h = node?.offsetHeight ?? 220;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const nextX = clamp(startPos.current.x + dx, 0, vw - w);
    const nextY = clamp(startPos.current.y + dy, 0, vh - h);

    setPos({ x: nextX, y: nextY });
  };

  const endDrag = () => setDragging(false);

  useEffect(() => {
    const handleMove = (e) => onPointerMove(e);
    const handleUp = () => endDrag();

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [dragging]);

  const handleStop = async () => {
    try {
      await stopReadingSession();
    } catch (error) {
      console.error('Failed to stop reading session:', error);
    }
  };

  const handlePause = () => {
    try {
      pauseReadingSession();
    } catch (error) {
      console.error('Failed to pause reading session:', error);
    }
  };

  const handleResume = () => {
    try {
      resumeReadingSession();
    } catch (error) {
      console.error('Failed to resume reading session:', error);
    }
  };

  // Don't render if no active session
  if (!activeSession) return null;

  if (isMinimized) {
    return (
      <div
        ref={timerRef}
        onClick={() => setIsMinimized(false)}
        data-draggable="true"
        onPointerDown={onPointerDown}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          transform: `translate(${pos.x}px, ${pos.y}px)`,
          zIndex: 1000,
          background: 'var(--sq-gradient-brand)',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '20px',
          cursor: dragging ? 'grabbing' : 'grab',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '14px',
          fontWeight: '600',
          fontFamily: 'monospace',
          userSelect: 'none',
          transition: dragging ? 'none' : 'all 0.2s ease',
          opacity: dragging ? 0.95 : 1
        }}
        onMouseEnter={(e) => !dragging && (e.target.style.transform = `translate(${pos.x}px, ${pos.y}px) scale(1.05)`)}
        onMouseLeave={(e) => !dragging && (e.target.style.transform = `translate(${pos.x}px, ${pos.y}px) scale(1)`)}
      >
        <Icon name={isPaused ? 'pause' : 'book'} size={16} /> {formatTime(elapsedTime)}
      </div>
    );
  }

  return (
    <div
      ref={timerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        zIndex: 1000,
        background: 'var(--md-sys-color-surface)',
        border: '2px solid var(--md-sys-color-primary)',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        minWidth: '280px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        cursor: dragging ? 'grabbing' : 'default',
        opacity: dragging ? 0.95 : 1,
        transition: dragging ? 'none' : 'box-shadow 0.2s ease'
      }}
    >
      {/* Header - Draggable */}
      <div
        data-draggable="true"
        onPointerDown={onPointerDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}><Icon name="book" size={20} /></span>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--md-sys-color-on-surface)'
            }}>
              Reading Session
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--md-sys-color-on-surface-variant)',
              opacity: 0.7
            }}>
              Drag to move
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsMinimized(true)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            color: 'var(--md-sys-color-on-surface-variant)',
            padding: '4px'
          }}
          title="Minimize"
        >
          <Icon name="minimize" size={18} />
        </button>
      </div>

      {/* Book Info */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--md-sys-color-on-surface)',
          marginBottom: '4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {activeSession.book?.title || 'Unknown Book'}
        </div>
        <div style={{
          fontSize: '12px',
          color: 'var(--md-sys-color-on-surface-variant)'
        }}>
          by {activeSession.book?.author || 'Unknown Author'}
        </div>
      </div>

      {/* Timer Display */}
      <div style={{
        background: 'var(--sq-gradient-brand)',
        color: '#fff',
        padding: '16px',
        borderRadius: '12px',
        textAlign: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          fontSize: '32px',
          fontWeight: '700',
          fontFamily: 'monospace',
          marginBottom: '4px'
        }}>
          {formatTime(elapsedTime)}
        </div>
        <div style={{
          fontSize: '12px',
          opacity: 0.9
        }}>
          Reading Time
        </div>
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '8px'
      }}>
        <MD3Button
          variant="outlined"
          onClick={() => setIsMinimized(true)}
          style={{ flex: 1, fontSize: '12px', padding: '8px 12px' }}
        >
          <Icon name="minimize" size={14} /> Minimize
        </MD3Button>
        <MD3Button
          variant={isPaused ? "filled" : "outlined"}
          onClick={isPaused ? handleResume : handlePause}
          style={{ 
            flex: 1, 
            fontSize: '12px', 
            padding: '8px 12px',
            background: isPaused ? 'var(--sq-success)' : 'transparent',
            color: isPaused ? '#fff' : 'var(--md-sys-color-on-surface)'
          }}
        >
          {isPaused ? (
            <>
              <Icon name="play_arrow" size={14} /> Resume
            </>
          ) : (
            <>
              <Icon name="pause" size={14} /> Pause
            </>
          )}
        </MD3Button>
        <MD3Button
          variant="filled"
          onClick={handleStop}
          style={{ 
            flex: 1,
            fontSize: '12px',
            padding: '8px 12px',
            background: 'var(--md-sys-color-error)',
            color: 'var(--md-sys-color-on-error)'
          }}
        >
          <Icon name="stop" size={14} /> Stop
        </MD3Button>
      </div>
    </div>
  );
};

export default ReadingSessionTimer;
